<template>
  <div class="row" v-if="$gate.isAdmin()">
    <div class="toolbar py-6">
      <div class="container-fluid d-flex align-items-center">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <span class="h-20px border-gray-200 border-start mx-3"></span>
          <ol class="breadcrumb breadcrumb-dot text-muted fs-6 fw-semibold">
            <li class="breadcrumb-item pe-3">
              <a @click.prevent="onBackClick" style="cursor: pointer;" class="pe-3">Înapoi</a>
            </li>
            <li class="breadcrumb-item pe-3 text-muted">Căutare firme</li>
          </ol>
          <span class="h-20px border-gray-200 border-start mx-3"></span>
        </div>
      </div>
    </div>

    <div class="content d-flex flex-column flex-column-fluid">
      <div class="container-fluid">
        <div class="card card-flush">
          <div class="card-header align-items-center py-5 gap-2 gap-md-5">
            <div class="card-title">
              <div class="d-flex align-items-center position-relative my-1">
                <span class="me-5 fs-4 fw-bold">Căutare firme</span>
              </div>
            </div>

            <div class="card-toolbar flex-row-fluid justify-content-end gap-2 gap-md-5">
              <!-- Compact Module Navigation when firm is selected -->
              <div v-if="selectedFirma" class="d-flex align-items-center gap-2 me-4 border-end pe-4 d-none d-lg-flex">
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2"
                  :class="{ 'active': activeModule === 'facturare' }" @click="selectModule('facturare')">
                  <i class="fa-solid fa-file-invoice me-1"></i> Facturare
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2"
                  :class="{ 'active': activeModule === 'contabilitate' }" @click="selectModule('contabilitate')">
                  <i class="fa-solid fa-calculator me-1"></i> Contabilitate
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2"
                  :class="{ 'active': activeModule === 'angajati' }" @click="selectModule('angajati')">
                  <i class="fa-solid fa-users me-1"></i> Angajați
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2"
                  :class="{ 'active': activeModule === 'fisiere' }" @click="selectModule('fisiere')">
                  <i class="fa-solid fa-folder-open me-1"></i> Fișiere
                </button>
              </div>

              <!-- Collapsed search button -->
              <button v-if="searchCollapsed && selectedFirma" type="button"
                class="btn btn-icon btn-light btn-active-light-primary me-3" @click="expandSearch"
                :title="'Caută din nou'" aria-label="Deschide căutarea">
                <i class="fa-solid fa-magnifying-glass fs-2"></i>
              </button>

              <!-- Full search bar -->
              <div v-else class="smart-search w-100 d-flex justify-content-end">
                <div class="smart-search__box">
                  <i class="smart-search__icon" aria-hidden="true"></i>
                  <input type="text" class="smart-search__input" v-model.trim="searchTerm"
                    placeholder="Caută după user, ID user, ID firmă, denumire sau CUI..."
                    @keydown.enter.prevent="triggerSearch" @keydown.esc.prevent="clearSearch" @input="onSearchInput"
                    @focus="focused = true" @blur="onBlur" />
                  <button type="button" class="smart-search__clear" v-show="searchTerm" @mousedown.prevent
                    @click="clearSearch" aria-label="Șterge căutarea" title="Șterge">
                    ×
                  </button>
                  <div class="smart-search__spinner" v-show="searching" aria-hidden="true"></div>

                  <!-- Results dropdown -->
                  <div class="smart-results" v-show="focused && (searchResults.length || searchEmpty)">
                    <div v-if="searchResults.length">
                      <div class="smart-results__header">Rezultate ({{ searchResults.length }})</div>
                      <ul class="smart-results__list">
                        <li v-for="r in searchResults" :key="`rs_${r.id}`" class="smart-results__item"
                          @mousedown.prevent @click="openFirma(r)">
                          <div class="d-flex align-items-center w-100">
                            <div class="symbol symbol-circle symbol-50px overflow-hidden me-3 flex-shrink-0">
                              <div v-if="r.sigla" class="symbol-label">
                                <img :src="`/api/fisiere/sigla-firma/${r.id}`" alt="Sigla"
                                  style="width: 100%; height: 100%; object-fit: cover;" />
                              </div>
                              <div v-else class="symbol-label fs-6 bg-light-primary text-primary">
                                {{ (r.denumire || '?').charAt(0) }}
                              </div>
                            </div>
                            <div class="flex-grow-1 min-w-0">
                              <div class="text-truncate fw-semibold">{{ r.denumire }} <span class="text-muted">#{{ r.id
                                  }}</span></div>
                              <div class="text-muted small text-truncate">CUI: {{ r.cui || '-' }}<span
                                  v-if="r.user_name"> · Utilizator: {{ r.user_name }}</span></div>
                            </div>
                            <span class="badge badge-light ms-3">Deschide</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div v-else-if="searchEmpty" class="smart-results__empty">Niciun rezultat</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body pt-0">
            <div v-if="selectedFirma"
              class="alert alert-primary d-flex align-items-center justify-content-between py-2 px-4 mb-0 min-h-40px">
              <div class="d-flex align-items-center overflow-hidden">
                <div class="symbol symbol-circle symbol-25px overflow-hidden me-3 flex-shrink-0">
                  <div v-if="selectedFirma.sigla" class="symbol-label">
                    <img :src="`/api/fisiere/sigla-firma/${selectedFirma.id}`" alt="Sigla"
                      style="width:100%;height:100%;object-fit:cover;" />
                  </div>
                  <div v-else class="symbol-label fs-8 bg-light-primary text-primary fw-bold">
                    {{ (selectedFirma.denumire || '?').charAt(0) }}
                  </div>
                </div>
                <div class="text-truncate">
                  <span class="fw-bold fs-7 me-2">Firma: {{ selectedFirma.denumire }}</span>
                  <span class="text-muted fs-8 me-2">#{{ selectedFirma.id }}</span>
                  <span class="text-muted fs-8 d-none d-md-inline" v-if="selectedFirma.cui">CUI: {{ selectedFirma.cui
                  }}</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2 flex-shrink-0">
                <button type="button" class="btn btn-icon btn-sm btn-light h-25px w-25px" @click="toggleSearch"
                  title="Caută firme">
                  <i class="fa-solid fa-magnifying-glass fs-8"></i>
                </button>
                <button type="button" class="btn btn-sm btn-light-danger fw-bold fs-8 py-1 px-3 h-25px"
                  @click="clearSelection">
                  Șterge selecția
                </button>
              </div>
            </div>

            <!-- Default Firms List -->
            <div v-else>
              <div class="table-responsive">
                <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                  <thead>
                    <tr class="fw-bold text-muted">
                      <th class="min-w-150px">Firmă</th>
                      <th class="min-w-100px">CUI</th>
                      <th class="min-w-150px">Utilizator</th>
                      <th class="min-w-100px text-end">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in (firmeData.data || [])" :key="f.id">
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="symbol symbol-45px me-5">
                            <img v-if="f.sigla" :src="`/api/fisiere/sigla-firma/${f.id}`" alt="Sigla" />
                            <span v-else class="symbol-label fw-bold bg-light-primary text-primary">
                              {{ (f.denumire || '?').charAt(0) }}
                            </span>
                          </div>
                          <div class="d-flex justify-content-start flex-column">
                            <a href="#" class="text-dark fw-bold text-hover-primary fs-6"
                              @click.prevent="openFirma(f)">{{ f.denumire }}</a>
                            <span class="text-muted fw-semibold d-block fs-7">#{{ f.id }}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span class="text-muted fw-bold d-block fs-7">{{ f.cui || '-' }}</span>
                      </td>
                      <td>
                        <span class="text-muted fw-bold d-block fs-7">{{ f.user_name || '-' }}</span>
                        <span class="text-muted fw-semibold d-block fs-8">{{ f.user_email }}</span>
                      </td>
                      <td class="text-end">
                        <button class="btn btn-sm btn-light-primary" @click="openFirma(f)">Selectează</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div v-if="firmeData.total > firmeData.per_page" class="d-flex justify-content-center mt-4">
                <nav aria-label="Page navigation">
                  <ul class="pagination pagination-outline">
                    <li class="page-item" :class="{ disabled: firmeData.current_page === 1 }">
                      <a class="page-link" href="#" @click.prevent="fetchFirme(firmeData.current_page - 1)">Inapoi</a>
                    </li>
                    <li v-for="p in paginationPages" :key="p" class="page-item"
                      :class="{ active: firmeData.current_page === p }">
                      <a class="page-link" href="#" @click.prevent="fetchFirme(p)">{{ p }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: firmeData.current_page === firmeData.last_page }">
                      <a class="page-link" href="#" @click.prevent="fetchFirme(firmeData.current_page + 1)">Inainte</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>

        </div>

        <!-- Second area: Dynamic area for module content -->
        <div class="mt-6" v-if="selectedFirma || $route.params.firmaId">
          <div class="dynamic-content-area">
            <!-- Mobile Navigation (visible only on small screens) -->
            <div class="d-flex d-lg-none flex-wrap gap-2 mb-4 bg-white p-2 rounded shadow-sm border">
              <button type="button" class="btn btn-sm btn-light-primary w-100 mb-1"
                :class="{ 'active': activeModule === 'facturare' }" @click="selectModule('facturare')">
                <i class="fa-solid fa-file-invoice me-2"></i> Facturare
              </button>
              <button type="button" class="btn btn-sm btn-light-primary w-100 mb-1"
                :class="{ 'active': activeModule === 'contabilitate' }" @click="selectModule('contabilitate')">
                <i class="fa-solid fa-calculator me-2"></i> Contabilitate
              </button>
              <button type="button" class="btn btn-sm btn-light-primary w-100 mb-1"
                :class="{ 'active': activeModule === 'angajati' }" @click="selectModule('angajati')">
                <i class="fa-solid fa-users me-2"></i> Angajați
              </button>
              <button type="button" class="btn btn-sm btn-light-primary w-100 mb-1"
                :class="{ 'active': activeModule === 'fisiere' }" @click="selectModule('fisiere')">
                <i class="fa-solid fa-folder-open me-2"></i> Fișiere
              </button>
            </div>

            <div v-if="!activeModule" class="card card-flush py-10 text-center">
              <div class="text-muted fs-5">Alege un modul din bara de sus pentru a începe lucrul.</div>
            </div>

            <div v-else-if="activeModule === 'facturare'">
              <!-- Submodule Pills -->
              <div class="subnav-pills d-flex flex-wrap gap-2 mb-4 bg-white p-2 rounded shadow-sm border">
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'emise' }" @click="selectSubModule('emise')">
                  <i class="fa-solid fa-file-invoice-dollar me-1"></i> Facturi emise
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'primite' }" @click="selectSubModule('primite')">
                  <i class="fa-solid fa-file-import me-1"></i> Facturi primite
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'extrase' }" @click="selectSubModule('extrase')">
                  <i class="fa-solid fa-receipt me-1"></i> Extrase bancare
                </button>
              </div>

              <!-- Results within a card -->
              <div class="card card-flush mt-4">
                <div class="card-body">
                  <div v-if="subModule === 'emise'">
                    <FacturiEmise :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>

                  <div v-else-if="subModule === 'primite'">
                    <FacturiPrimite :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>

                  <div v-else-if="subModule === 'extrase'">
                    <ExtraseBancare :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else class="text-muted">Alege o secțiune pentru facturare.</div>
                </div>
              </div>
            </div>

            <div v-else-if="activeModule === 'contabilitate'">
              <!-- Submodule Pills -->
              <div class="subnav-pills d-flex flex-wrap gap-2 mb-4 bg-white p-2 rounded shadow-sm border">
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'registru_jurnal' }" @click="selectSubModule('registru_jurnal')">
                  <i class="fa-solid fa-book me-1"></i> Registru jurnal
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'balanta' }" @click="selectSubModule('balanta')">
                  <i class="fa-solid fa-scale-balanced me-1"></i> Balanță
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'raport_tva' }" @click="selectSubModule('raport_tva')">
                  <i class="fa-solid fa-file-lines me-1"></i> Raport TVA
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'fisa_cont' }" @click="selectSubModule('fisa_cont')">
                  <i class="fa-solid fa-list-ul me-1"></i> Fișă Cont
                </button>
              </div>

              <!-- Rezultate contabilitate -->
              <div class="card card-flush mt-4">
                <div class="card-body">
                  <div v-if="subModule === 'registru_jurnal'">
                    <RegistruJurnal :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else-if="subModule === 'balanta'">
                    <BalantaContabila :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else-if="subModule === 'raport_tva'">
                    <div class="alert alert-info mb-0">
                      Modulul de Raport TVA urmează să fie implementat.
                    </div>
                  </div>
                  <div v-else-if="subModule === 'fisa_cont'">
                    <FisaCont :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else class="text-muted">Alege o secțiune pentru contabilitate.</div>
                </div>
              </div>
            </div>

            <div v-else-if="activeModule === 'angajati'">
              <!-- Submodule Pills -->
              <div class="subnav-pills d-flex flex-wrap gap-2 mb-4 bg-white p-2 rounded shadow-sm border">
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'contracte' }" @click="selectSubModule('contracte')">
                  <i class="fa-solid fa-file-signature me-1"></i> Contracte
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'state' }" @click="selectSubModule('state')">
                  <i class="fa-solid fa-money-check-dollar me-1"></i> State de plată
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'pontaje' }" @click="selectSubModule('pontaje')">
                  <i class="fa-solid fa-clock me-1"></i> Pontaje
                </button>
              </div>

              <div class="card card-flush mt-4">
                <div class="card-body">
                  <div v-if="subModule === 'contracte'">
                    <div class="alert alert-info">Modulul de Contracte urmează să fie implementat.</div>
                  </div>
                  <div v-else-if="subModule === 'state'">
                    <div class="alert alert-info">Modulul de State de plată urmează să fie implementat.</div>
                  </div>
                  <div v-else-if="subModule === 'pontaje'">
                    <div class="alert alert-info">Modulul de Pontaje urmează să fie implementat.</div>
                  </div>
                  <div v-else class="text-muted">Alege o secțiune pentru angajați.</div>
                </div>
              </div>
            </div>
            <div v-else-if="activeModule === 'fisiere'">
              <!-- Submodule Pills -->
              <div class="subnav-pills d-flex flex-wrap gap-2 mb-4 bg-white p-2 rounded shadow-sm border">
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'iesiri' }" @click="selectSubModule('iesiri')">
                  <i class="fa-solid fa-file-export me-1"></i> Ieșiri
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'intrari' }" @click="selectSubModule('intrari')">
                  <i class="fa-solid fa-file-import me-1"></i> Intrări
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'extrase' }" @click="selectSubModule('extrase')">
                  <i class="fa-solid fa-receipt me-1"></i> Extrase
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'altedocumente' }" @click="selectSubModule('altedocumente')">
                  <i class="fa-solid fa-folder me-1"></i> Alte doc.
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'tot' }" @click="selectSubModule('tot')">
                  <i class="fa-solid fa-layer-group me-1"></i> Tot
                </button>
                <button type="button" class="btn btn-sm btn-light-primary btn-active-primary px-3 py-2 fw-bold"
                  :class="{ 'active': subModule === 'status_luni' }" @click="selectSubModule('status_luni')">
                  <i class="fa-solid fa-calendar-check me-1"></i> Gestiune luni
                </button>
              </div>

              <!-- Results area for files submodules -->
              <div class="card card-flush mt-4">
                <div class="card-body">
                  <div v-if="subModule === 'iesiri'">
                    <AdminIncarcariIesiri :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else-if="subModule === 'intrari'">
                    <AdminIncarcariIntrari :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else-if="subModule === 'extrase'">
                    <AdminIncarcariExtrase :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else-if="subModule === 'altedocumente'">
                    <AdminIncarcariAlteDoc :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else-if="subModule === 'tot'">
                    <AdminIncarcariToate :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else-if="subModule === 'status_luni'">
                    <AdminGestionareLuni :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                  </div>
                  <div v-else class="text-muted">Alege o secțiune pentru fișiere.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FacturiEmise from './work_components/FacturiEmise.vue';
import FacturiPrimite from './work_components/FacturiPrimite.vue';
import ExtraseBancare from './work_components/ExtraseBancare.vue';
import AdminIncarcariIntrari from './work_components/Admin_IncarcariIntrari.vue';
import AdminIncarcariIesiri from './work_components/Admin_IncarcariIesiri.vue';
import AdminIncarcariExtrase from './work_components/Admin_IncarcariExtrase.vue';
import AdminIncarcariAlteDoc from './work_components/Admin_IncarcariAlteDoc.vue';
import AdminIncarcariToate from './work_components/Admin_IncarcariToate.vue';
import AdminGestionareLuni from './work_components/Admin_GestionareLuni.vue';
import RegistruJurnal from './work_components/RegistruJurnal.vue';
import FisaCont from '../Contabilitate/FisaCont.vue';
import BalantaContabila from '../Contabilitate/BalantaContabila.vue';

export default {
  components: { FacturiEmise, FacturiPrimite, AdminIncarcariIntrari, AdminIncarcariIesiri, AdminIncarcariExtrase, AdminIncarcariAlteDoc, AdminIncarcariToate, AdminGestionareLuni, ExtraseBancare, RegistruJurnal, FisaCont, BalantaContabila },
  data() {
    return {
      // doar starea de search
      searchTerm: '',
      searching: false,
      searchResults: [],
      searchEmpty: false,
      showSuggestions: true,
      _searchDebounce: null,
      focused: false,
      _blurTimer: null,
      selectedFirma: null,
      searchCollapsed: false,
      activeModule: null,
      subModule: null,
      expandedRows: [],
      // Tooltip custom
      tooltipData: {
        visible: false,
        text: '',
        activeId: null
      },
      firmeData: {},
    };
  },
  computed: {
    paginationPages() {
      if (!this.firmeData || !this.firmeData.last_page) return [];
      const total = this.firmeData.last_page;
      const current = this.firmeData.current_page;
      const pages = [];
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);

      if (current <= 3) end = Math.min(total, 5);
      if (current > total - 3) start = Math.max(1, total - 4);

      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    subModule(newVal, oldVal) {
      // Logic for subModule changes if needed
    },
    $route: {
      immediate: false,
      handler() {
        this.applyRoute();
      }
    },
  },
  mounted() {
    this.applyRoute();
    if (!this.selectedFirma && !this.$route.params.firmaId) {
      this.fetchFirme();
    }
  },
  beforeUnmount() {

  },
  methods: {
    fetchFirme(page = 1) {
      axios.get('/api/administrare/work/firme-toate', { params: { page } })
        .then(res => {
          this.firmeData = res.data;
        })
        .catch(err => {
          console.error("Error fetching firms:", err);
        });
    },
    // --- ROUTE HELPERS ---
    routePathFor(firmaId, module, submodule) {
      const parts = ['/administrare/work'];
      if (firmaId) parts.push(String(firmaId));
      if (module) parts.push(String(module));
      if (submodule) parts.push(String(submodule));
      return parts.join('/');
    },
    navigate(firmaId, module, submodule) {
      const path = this.routePathFor(firmaId, module, submodule);
      if (this.$route && this.$route.path !== path) {
        this.$router.push(path).catch(() => { });
      }
    },
    normalizeFirma(raw) {
      if (!raw || typeof raw !== 'object') return null;
      const id = raw.id || raw.firma_id || raw.ID || null;
      return {
        id,
        denumire: raw.denumire || raw.denumire_firma || raw.nume || raw.name || '',
        cui: raw.cui || raw.cui_firma || raw.cif || raw.vat || '',
        user_name: raw.user_name || raw.user || raw.utilizator || '',
        sigla: raw.sigla || raw.logo || raw.sigla_url || null,
      };
    },
    async fetchFirmaById(id) {
      const safeSet = (obj) => {
        const f = this.normalizeFirma(obj);
        if (f && f.id) {
          this.selectedFirma = f;
          return true;
        }
        return false;
      };
      try {
        // 1) Primary: REST-style endpoint
        const res1 = await axios.get(`/api/administrare/work/firma/${id}`);
        if (res1 && res1.data && (safeSet(res1.data.firma || res1.data))) return;
      } catch (_) { /* fallthrough */ }
      try {
        // 2) Variant: query param
        const res2 = await axios.get('/api/administrare/work/firma', { params: { id } });
        if (res2 && res2.data && (safeSet(res2.data.firma || res2.data))) return;
      } catch (_) { /* fallthrough */ }
      try {
        // 3) Fallback: search API with explicit id: filter
        const res3 = await axios.put('/api/administrare/work/cautare', { q: `id:${id}` });
        const rows = (res3 && res3.data && (res3.data.firme || res3.data)) || [];
        if (Array.isArray(rows) && rows.length > 0 && safeSet(rows[0])) return;
      } catch (_) { /* fallthrough */ }
      // As a last resort keep at least the id so the page works
      this.selectedFirma = { id: Number(id) };
    },
    applyRoute() {
      const params = (this.$route && this.$route.params) || {};
      const firmaId = params.firmaId ? Number(params.firmaId) : null;
      let module = params.module || null;
      let sub = params.submodule || null;

      // Ensure firma is loaded if we have an id in route
      if (firmaId && (!this.selectedFirma || Number(this.selectedFirma.id) !== firmaId)) {
        this.fetchFirmaById(firmaId);
      }

      // Normalize to internal keys used by the UI
      // Route uses: facturare/facturi_emise | facturare/facturi_primite
      if (sub === 'facturi_emise') sub = 'emise';
      if (sub === 'facturi_primite') sub = 'primite';

      // If we have a submodule but no module in URL, default module is facturare
      if (!module && sub) module = 'facturare';

      if (module) {
        this.activeModule = module;
      } else {
        this.activeModule = null;
      }

      if (sub) {
        this.subModule = sub;
      } else {
        this.subModule = null;
      }

      if (firmaId) {
        this.searchCollapsed = true;
      }
    },
    // --- SEARCH ---
    onSearchInput() {
      const term = this.searchTerm || '';
      this.showSuggestions = term.length === 0;
      if (this._searchDebounce) clearTimeout(this._searchDebounce);
      if (term.length < 2) {
        this.searchResults = [];
        this.searchEmpty = false;
        this.searching = false;
        return;
      }
      this._searchDebounce = setTimeout(() => this.triggerSearch(), 350);
    },
    triggerSearch() {
      const term = (this.searchTerm || '').trim();
      if (!term || term.length < 2) return;
      this.searching = true;

      // Controller separat pentru Work.vue
      axios.put('/api/administrare/work/cautare', { q: term })
        .then((res) => {
          const rows = res.data && (res.data.firme || res.data) || [];
          this.applySearchResults(rows, term);
        })
        .finally(() => { this.searching = false; });
    },
    clearSearch() {
      this.searchTerm = '';
      this.searchResults = [];
      this.searchEmpty = false;
      this.showSuggestions = true;
      this.searching = false;
    },
    applySearchResults(rows, term) {
      const arr = Array.isArray(rows) ? rows : [];
      this.searchResults = arr.slice(0, 50);
      this.searchEmpty = this.searchResults.length === 0;
    },
    // opțional: potrivire locală dacă vei vrea fallback
    matchFirma(f, term) {
      if (!f) return false;
      const t = term.toLowerCase();
      if (t.startsWith('id:')) return String(f.id || '') === t.slice(3).trim();
      if (t.startsWith('cui:')) return String(f.cui || '').toLowerCase().includes(t.slice(4).trim());
      if (t.startsWith('uid:')) return String(f.user_id || '').toLowerCase() === t.slice(4).trim();
      if (t.startsWith('user:')) return String(f.user_name || '').toLowerCase().includes(t.slice(5).trim());
      const hay = [
        String(f.id || ''),
        String(f.denumire || '').toLowerCase(),
        String(f.cui || '').toLowerCase(),
        String(f.user_name || '').toLowerCase(),
        String(f.user_email || '').toLowerCase(),
      ];
      return hay.some(x => x && x.includes(t));
    },
    openFirma(f) {
      this.selectedFirma = f || null;
      this.activeModule = null;
      this.subModule = null;
      this.$emit && this.$emit('select-firma', f);
      // collapse search UI
      this.searchCollapsed = true;
      this.navigate(f ? f.id : null);
    },
    toggleSearch() {
      this.searchCollapsed = !this.searchCollapsed;
    },
    expandSearch() {
      this.searchCollapsed = false;
    },
    clearSelection() {
      this.selectedFirma = null;
      this.activeModule = null;
      this.subModule = null;
      this.searchCollapsed = false;
      this.searchTerm = '';
      this.navigate(null);
      this.fetchFirme();

      // focus back into search input
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.smart-search__input');
        if (el) { el.focus(); }
      });
    },
    onBlur() {
      // întârziem puțin ca să permită click pe item din listă
      if (this._blurTimer) clearTimeout(this._blurTimer);
      this._blurTimer = setTimeout(() => { this.focused = false; }, 150);
    },
    selectModule(name) {
      if (!this.selectedFirma) return; // guard
      if (this.activeModule === name) {
        // toggle off the module (and any submodule)
        this.activeModule = null;
        this.subModule = null;
        this.navigate(this.selectedFirma.id, null, null);
        return;
      }
      // activate the module
      this.activeModule = name;
      this.subModule = null;
      this.navigate(this.selectedFirma.id, name, null);
    },
    selectSubModule(name) {
      if (!this.selectedFirma) return;
      if (this.subModule === name) {
        // toggle off submodule, stay at module level
        this.subModule = null;
        const module = this.activeModule || 'facturare';
        this.navigate(this.selectedFirma.id, module, null);
        return;
      }
      // activate submodule
      this.subModule = name;
      const module = this.activeModule || 'facturare';
      const subroute = (name === 'emise') ? 'facturi_emise' : (name === 'primite' ? 'facturi_primite' : name);
      this.navigate(this.selectedFirma.id, module, subroute);
    },
    onBackClick() {
      if (this.subModule) {
        // If a submodule is active, clear it and go to module level
        const module = this.activeModule || 'facturare';
        this.subModule = null;
        this.navigate(this.selectedFirma?.id, module, null);
        return;
      }
      if (this.activeModule) {
        // If a module is active, clear it and go to firm root
        this.activeModule = null;
        this.navigate(this.selectedFirma?.id, null, null);
        return;
      }
      if (this.selectedFirma) {
        // If a firm is selected, clear selection and go to base work
        this.clearSelection();
        return;
      }
      // fallback to history back if nothing to clear
      if (this.$router && window && window.history && window.history.length > 1) {
        this.$router.go(-1);
      }
    },

  },
  created() {

  },
}
</script>

<style lang="scss" scoped>
#kt_docs_search_handler_basic .list-group-item {
  cursor: pointer;
}

#kt_docs_search_handler_basic .list-group-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

#kt_docs_search_handler_basic .form-control {
  position: relative;
}

.smart-search {
  max-width: 100%;
}

.smart-search__box {
  position: relative;
  width: 100%;
}

.smart-search__input {
  width: 100%;
  height: 64px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f9fafb;
  padding: 0 60px 0 56px;
  /* space for icons */
  font-size: 1.125rem;
  /* 18px */
  transition: box-shadow .2s ease, background .2s ease, border-color .2s ease;
  color: #111;
}

.smart-search__input:focus {
  outline: none;
  background: #ffffff;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  border-color: rgba(0, 0, 0, 0.12);
}

/* Theme-aware colors for search input (light vs dark) */
.smart-search__input::placeholder {
  color: rgba(0, 0, 0, .55);
}

[data-bs-theme="dark"] .smart-search__input {
  color: #f1f3f5 !important;
  background: #1f1f1f;
  border-color: rgba(255, 255, 255, .15);
}

[data-bs-theme="dark"] .smart-search__input:focus {
  background: #151515;
  border-color: rgba(255, 255, 255, .2);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
}

[data-bs-theme="dark"] .smart-search__input::placeholder {
  color: rgba(255, 255, 255, .6);
}

.smart-search__icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  opacity: .6;
}

.smart-search__icon::before {
  content: '';
  display: block;
  width: 22px;
  height: 22px;
  border: 2px solid currentColor;
  border-radius: 50%;
  color: #5e6278;
  opacity: .7;
  position: relative;
}

.smart-search__icon::after {
  content: '';
  position: absolute;
  left: 15px;
  top: 15px;
  width: 9px;
  height: 2px;
  background: currentColor;
  transform: rotate(45deg);
}

.smart-search__clear {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 0;
  background: #eef2f7;
  color: #1e1e2d;
  opacity: .85;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
}

.smart-search__clear:hover {
  opacity: 1;
  background: #e8edf5;
}

.smart-search__spinner {
  position: absolute;
  right: 60px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-top-color: #5e81f4;
  border-radius: 50%;
  animation: smart-spin 1s linear infinite;
}

@keyframes smart-spin {
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}

.smart-results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 10px);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.08);
  max-height: 60vh;
  overflow: auto;
  z-index: 50;
}

.smart-results__header {
  padding: 10px 14px;
  font-weight: 600;
  color: #5e6278;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.smart-results__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.smart-results__item {
  padding: 10px 14px;
  cursor: pointer;
}

.smart-results__item:hover {
  background: #f9fafb;
}

.smart-results__empty {
  padding: 18px 14px;
  color: #7e8299;
  text-align: center;
}

.btn.btn-icon {
  width: 42px;
  height: 42px;
}

.tile-btn {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 140px;
  height: 110px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  color: #1e1e2d;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}

.tile-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  border-color: rgba(0, 0, 0, 0.1);
}

.tile-btn span {
  font-weight: 600;
}

.tile-btn--active {
  border-color: #5e81f4;
  box-shadow: 0 10px 26px rgba(94, 129, 244, 0.2);
}

.table-row-bordered> :not(caption)>*>* {
  border-bottom-width: 1px;
}

/* Compact tiles when a module/submodule is selected */
.tile-btn i {
  font-size: 2rem;
}

.subnav-pills .btn {
  transition: all 0.2s ease;
}

.subnav-pills .btn.btn-primary {
  box-shadow: 0 4px 10px rgba(94, 129, 244, 0.2);
}

/* Edge-to-edge table area */
.edge-to-edge {
  /* pull the content to the edges as much as possible inside container-fluid */
  margin-left: -12px;
  margin-right: -12px;
}

@media (min-width: 992px) {
  .edge-to-edge {
    margin-left: -24px;
    margin-right: -24px;
  }
}

@media (min-width: 1400px) {
  .edge-to-edge {
    margin-left: -32px;
    margin-right: -32px;
  }
}
</style>
